import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, first, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  CreatePunchRound,
  CreatePunchRoundFailure,
  CreatePunchRoundSuccess,
  DeletePunchRound,
  DeletePunchRoundFailure,
  DeletePunchRoundSuccess,
  LoadPunchRoundById,
  LoadPunchRoundByIdFailure,
  LoadPunchRoundByIdSuccess,
  LoadPunchRoundFormMcps,
  LoadPunchRoundFormMcpsFailure,
  LoadPunchRoundFormMcpsSuccess,
  LoadPunchRoundMcpScope,
  LoadPunchRoundMcpScopeFailure,
  LoadPunchRoundMcpScopeSuccess,
  LoadPunchRounds,
  LoadPunchRoundsFailure,
  LoadPunchRoundsSuccess,
  LoadCurrentCpCsa,
  LoadCurrentMcpCsa,
  PunchRoundActionTypes,
  SendPunchRoundInvites,
  SendPunchRoundInvitesFailure,
  SendPunchRoundInvitesSuccess,
  FindPunchRoundSchedule,
  FindPunchRoundScheduleFailure,
  FindPunchRoundScheduleSuccess,
  UpdatePunchRound,
  UpdatePunchRoundCheckSheetAssignmentStatus,
  UpdatePunchRoundCheckSheetAssignmentStatusFailure,
  UpdatePunchRoundCheckSheetAssignmentStatusSuccess,
  UpdatePunchRoundFailure,
  UpdatePunchRoundSuccess,
} from '@completion/actions';
import { State } from '@completion/reducers';
import { getCurrentCpId, getCurrentMcpId, getCurrentProject, getPunchRounds } from '@completion/selectors';
import { PunchRoundService } from '@completion/services';
import { AttendeeStatus, CheckSheetAssignment, McPackageSummary, Project, PunchRound, PunchRoundScheduleResult } from '@completion/models';
import { trackResource } from './resource-fetch.util';
import { NotificationService } from '../../services/notification.service';

/**
 * Derive durationHours from two ISO datetime strings.
 * Returns undefined if either value is missing or invalid.
 */
function calcDurationHours(start: string | undefined, end: string | undefined): number | undefined {
  if (!start || !end) return undefined;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (isNaN(ms) || ms <= 0) return undefined;
  return ms / (1000 * 60 * 60);
}

/**
 * When the backend enriches a response with Graph event data (`eventStart`,
 * `eventEnd`, `eventLocation`), derive the canonical DB fields from them:
 *
 *  - `scheduledAt`  ← `eventStart`  (Graph is the source of truth for meeting time)
 *  - `durationHours` ← end − start  in hours
 *  - `meetingPoint`  ← `eventLocation` when non-empty, else keep existing value
 *
 * Returns the round unchanged if no Graph event time data is present.
 */
function applyGraphEventTimes(round: PunchRound): PunchRound {
  if (!round.eventStart || !round.eventEnd) return round;

  const durationHours = calcDurationHours(round.eventStart, round.eventEnd);
  const meetingPoint = round.eventLocation?.trim() || round.meetingPoint;

  return {
    ...round,
    scheduledAt: round.eventStart,
    ...(durationHours != null ? { durationHours } : {}),
    ...(meetingPoint != null ? { meetingPoint } : {}),
  };
}

/**
 * Returns true when the freshly loaded round contains Graph-driven changes
 * that differ from the cached version and should be persisted to the DB.
 */
function hasGraphUpdates(fresh: PunchRound, cached: PunchRound | undefined): boolean {
  // Only consider rounds that have an associated Graph calendar event
  if (!fresh.graphEventId) return false;
  // Nothing to compare against on first load
  if (!cached) return false;

  // Compare attendee statuses (sort by email for order-insensitive comparison)
  const sortByEmail = (a: AttendeeStatus, b: AttendeeStatus) => (a.email ?? '').localeCompare(b.email ?? '');
  const freshStatuses = JSON.stringify([...(fresh.attendeeStatuses ?? [])].sort(sortByEmail));
  const cachedStatuses = JSON.stringify([...(cached.attendeeStatuses ?? [])].sort(sortByEmail));
  if (freshStatuses !== cachedStatuses) return true;

  // If Graph event times are present, compare fresh eventStart/End against the
  // CACHED eventStart/End — not against scheduledAt.
  // This prevents overwriting a user's app-side edit: if the user changed
  // scheduledAt in this app, cached.eventStart still equals fresh.eventStart
  // (Outlook hasn't moved), so no sync is triggered.
  if (fresh.eventStart && fresh.eventStart !== cached.eventStart) return true;

  const freshDuration = calcDurationHours(fresh.eventStart, fresh.eventEnd);
  const cachedDuration = calcDurationHours(cached.eventStart, cached.eventEnd);
  if (freshDuration != null && freshDuration !== cachedDuration) return true;

  // Compare meeting location (eventLocation takes precedence if non-empty)
  const freshLocation = fresh.eventLocation?.trim() || fresh.meetingPoint;
  if ((freshLocation ?? '') !== (cached.meetingPoint ?? '')) return true;

  return false;
}
@Injectable()
export class PunchRoundEffect {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<State>,
    private readonly punchRoundService: PunchRoundService,
    private readonly toastr: NotificationService,
  ) {}
  loadPunchRounds$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadPunchRounds>(PunchRoundActionTypes.LoadPunchRounds),
      withLatestFrom(this.store.select(getCurrentProject), this.store.select(getCurrentCpId)),
      trackResource<PunchRound[]>({
        actions$: this.actions$,
        store: this.store,
        service: this.punchRoundService,
        method: 'getPunchRounds',
        serviceArgs: ([_, project, cpId]) => [project?.id, cpId],
        onSuccess: (res) => new LoadPunchRoundsSuccess(res),
        onError: (error) => new LoadPunchRoundsFailure(error),
      }),
    ),
  );
  loadPunchRoundById$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadPunchRoundById>(PunchRoundActionTypes.LoadPunchRoundById),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<PunchRound>({
        actions$: this.actions$,
        store: this.store,
        service: this.punchRoundService,
        method: 'getPunchRoundById',
        serviceArgs: ([action, project]) => [project?.id, (action as LoadPunchRoundById).punchRoundId],
        onSuccess: (res) => new LoadPunchRoundByIdSuccess(res),
        onError: (error) => new LoadPunchRoundByIdFailure(error),
      }),
    ),
  );
  createPunchRound$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CreatePunchRound>(PunchRoundActionTypes.CreatePunchRound),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<PunchRound>({
        actions$: this.actions$,
        store: this.store,
        service: this.punchRoundService,
        method: 'createPunchRound',
        serviceArgs: ([action, project]) => [project?.id, (action as CreatePunchRound).punchRound],
        onSuccess: (res) => new CreatePunchRoundSuccess(res),
        onError: (error) => new CreatePunchRoundFailure(error),
      }),
    ),
  );
  updatePunchRound$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdatePunchRound>(PunchRoundActionTypes.UpdatePunchRound),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<PunchRound>({
        actions$: this.actions$,
        store: this.store,
        service: this.punchRoundService,
        method: 'updatePunchRound',
        serviceArgs: ([action, project]) => [project?.id, (action as UpdatePunchRound).punchRound],
        onSuccess: (res) => new UpdatePunchRoundSuccess(res),
        onError: (error) => new UpdatePunchRoundFailure(error),
      }),
    ),
  );
  updateCheckSheetAssignmentStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdatePunchRoundCheckSheetAssignmentStatus>(PunchRoundActionTypes.UpdatePunchRoundCheckSheetAssignmentStatus),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<void>({
        actions$: this.actions$,
        store: this.store,
        service: this.punchRoundService,
        method: 'updateCheckSheetAssignmentStatus',
        serviceArgs: ([action, project]) => [
          project?.id,
          (action as UpdatePunchRoundCheckSheetAssignmentStatus).punchRoundId,
          (action as UpdatePunchRoundCheckSheetAssignmentStatus).checkSheetAssignmentId,
          (action as UpdatePunchRoundCheckSheetAssignmentStatus).status,
        ],
        onSuccess: () => new UpdatePunchRoundCheckSheetAssignmentStatusSuccess(),
        onError: (error) => new UpdatePunchRoundCheckSheetAssignmentStatusFailure(error),
      }),
    ),
  );
  reloadCurrentCheckSheetAssignmentAfterStatusUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdatePunchRoundCheckSheetAssignmentStatusSuccess>(PunchRoundActionTypes.UpdatePunchRoundCheckSheetAssignmentStatusSuccess),
      withLatestFrom(this.store.select(getCurrentMcpId)),
      map(([_, mcpId]) => (mcpId ? new LoadCurrentMcpCsa() : new LoadCurrentCpCsa())),
    ),
  );
  reloadPunchRoundsAfterCheckSheetAssignmentStatusUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdatePunchRoundCheckSheetAssignmentStatusSuccess>(PunchRoundActionTypes.UpdatePunchRoundCheckSheetAssignmentStatusSuccess),
      map(() => new LoadPunchRounds()),
    ),
  );
  rehydrateUpdatedPunchRound$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdatePunchRoundSuccess>(PunchRoundActionTypes.UpdatePunchRoundSuccess),
      filter(({ punchRound }) => punchRound?.id != null),
      map(({ punchRound }) => new LoadPunchRoundById(punchRound.id)),
    ),
  );
  deletePunchRound$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeletePunchRound>(PunchRoundActionTypes.DeletePunchRound),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<void>({
        actions$: this.actions$,
        store: this.store,
        service: this.punchRoundService,
        method: 'deletePunchRound',
        serviceArgs: ([action, project]) => [project?.id, (action as DeletePunchRound).punchRoundId],
        onSuccess: (_: void, sourceArgs?: any[]) => {
          const [maybeAction] = sourceArgs || [];
          const act = (Array.isArray(maybeAction) ? maybeAction[0] : maybeAction) as DeletePunchRound;
          return new DeletePunchRoundSuccess(act.punchRoundId);
        },
        onError: (error) => new DeletePunchRoundFailure(error),
      }),
    ),
  );
  /** Triggered on dialog open – fetch all MC packages for the current project. */
  loadFormMcps$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadPunchRoundFormMcps>(PunchRoundActionTypes.LoadPunchRoundFormMcps),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<McPackageSummary[]>({
        actions$: this.actions$,
        store: this.store,
        service: this.punchRoundService,
        method: 'getProjectMcps',
        serviceArgs: ([_, project]) => [project?.id],
        onSuccess: (mcps) => new LoadPunchRoundFormMcpsSuccess(mcps),
        onError: (error) => new LoadPunchRoundFormMcpsFailure(error),
      }),
    ),
  );
  /**
   * Triggered when the user changes MCP selection.
   * Delegates the parallel forkJoin to PunchRoundService.getMcpScope and tracks it via ResourceStart/Success/Failed.
   */
  loadMcpScope$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadPunchRoundMcpScope>(PunchRoundActionTypes.LoadPunchRoundMcpScope),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<CheckSheetAssignment[]>({
        actions$: this.actions$,
        store: this.store,
        service: this.punchRoundService,
        method: 'getMcpScope',
        serviceArgs: ([action, project]) => [project?.id, (action as LoadPunchRoundMcpScope).mcpIds],
        onSuccess: (csas) => new LoadPunchRoundMcpScopeSuccess(csas),
        onError: (error) => new LoadPunchRoundMcpScopeFailure(error),
      }),
    ),
  );

  /** Send invitations to the selected users for the given punch round. */
  sendInvites$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SendPunchRoundInvites>(PunchRoundActionTypes.SendPunchRoundInvites),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<PunchRound>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.punchRoundService,
        method: 'sendInvites',
        serviceArgs: ([action, project]) => [
          project?.id,
          (action as SendPunchRoundInvites).punchRoundId,
          (action as SendPunchRoundInvites).request,
        ],
        onSuccess: (res) => new SendPunchRoundInvitesSuccess(res),
        onError: (error) => new SendPunchRoundInvitesFailure(error),
      }),
    ),
  );

  /** Show a success toast when invitations are sent. */
  sendInvitesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<SendPunchRoundInvitesSuccess>(PunchRoundActionTypes.SendPunchRoundInvitesSuccess),
        tap(({ punchRound }) => {
          const count = punchRound.stakeholders?.length ?? 0;
          this.toastr.success(`Invitations sent successfully to ${count} participant${count !== 1 ? 's' : ''}.`);
        }),
      ),
    { dispatch: false },
  );

  findSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType<FindPunchRoundSchedule>(PunchRoundActionTypes.FindPunchRoundSchedule),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<PunchRoundScheduleResult>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.punchRoundService,
        method: 'findSchedule',
        serviceArgs: ([action, project]) => [project?.id, action.punchRoundId, action.request],
        onSuccess: (result) => new FindPunchRoundScheduleSuccess(result),
        onError: (error) => new FindPunchRoundScheduleFailure(error),
      }),
    ),
  );

  /**
   * When a punch round is loaded from the backend, check whether the Graph
   * calendar event was updated in Outlook (changed attendee responses, moved
   * meeting time, updated location). If so, persist the changes back to the DB
   * via UpdatePunchRound so the two systems stay in sync.
   *
   * Timing note: we capture the *old* cached round at the moment
   * LoadPunchRoundById fires (before the reducer replaces it), then wait for
   * the matching success action to compare old vs fresh.
   */
  syncGraphUpdates$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadPunchRoundById>(PunchRoundActionTypes.LoadPunchRoundById),
      withLatestFrom(this.store.select(getPunchRounds)), // snapshot BEFORE reducer runs
      mergeMap(([action, cachedRounds]) => {
        const cachedRound = cachedRounds.find((r) => r.id === action.punchRoundId);

        return this.actions$.pipe(
          ofType<LoadPunchRoundByIdSuccess>(PunchRoundActionTypes.LoadPunchRoundByIdSuccess),
          filter((success) => success.punchRound.id === action.punchRoundId),
          first(),
          filter((success) => hasGraphUpdates(success.punchRound, cachedRound)),
          map((success) => new UpdatePunchRound(applyGraphEventTimes(success.punchRound))),
        );
      }),
    ),
  );
}
